/**
 * GeneralSettingsCtrl controller
 * Show general settings
 */

import { GET_TIMEZONES } from 'app/core/constants'
import { has } from 'app/helpers/object'

/**
 * GeneralSettingsCtrl controller
 *
 * @class GeneralSettingsCtrl
 * @memberof mwApp/settings
 * @see mwApp/settings
 *
 * @ngdoc controller
 * @name mwApp/settings.controller:GeneralSettingsCtrl
 */
/* @ngInject */
export default function GeneralSettingsCtrl (
  $http,
  $q,
  $sce,
  $scope,
  $uibModal,
  InappSettings,
  Setting,
  alertLiteSvc,
  authSvc,
  mailSettings,
  smsServiceSettings,
  storageSvc
) {
  var vm = this

  /**
   * Loader indicator
   *
   * @type {boolean}
   */
  $scope.loading = true

  vm.smsServiceSettings = smsServiceSettings

  vm.smsServiceSettings.enabled = true

  /**
   * Can edit flag
   *
   * @memberof mwApp/settings.controller:GeneralSettingsCtrl
   * @type {Boolean}
   */
  vm.canEdit = authSvc.hasAccess('editGlobalGeneral')
  vm.isSuperAdmin = authSvc.hasAccess('superAdmin')
  vm.isSystemUser = storageSvc.local.get('userType') === 'SYSTEM'

  /**
   * Timezones
   *
   * @memberof mwApp/settings.controller:GeneralSettingsCtrl
   * @type {String[]}
   */
  GET_TIMEZONES().to(vm.timezones = [])
  vm.PAYMENT_LENGTH_UNLIMITED = window.ENV.APP_PAYMENT_LENGTH_UNLIMITED
  vm.APP_DEVICE_LINK_CODE_ENABLED = !!window.ENV.APP_DEVICE_LINK_CODE_ENABLED

  vm.senderTypes = [
    { value: 'ABBRIVIATED', label: 'Abbriviated' },
    { value: 'INTERNATIONAL', label: 'International' },
    { value: 'SENDER_NAME', label: 'Sender Name' }
  ]

  vm.valueForUnlimited = null

  /**
   * SMS provider settings
   */
  vm.smsGatewayEnabled = authSvc.hasEnabledFlag('SIGN_IN_BY_PHONE_ENABLED')
  vm.dcbSmsGatewayEnabled = authSvc.hasEnabledFlag('FEATURE_DCB_SMS_GATEWAY_ENABLED')

  vm.smsSettingsUrl = '/nora/api/settings/sms'
  vm.dcbSmsSettingsUrl = '/nora/api/settings/sms/dcb'

  vm.saveSmsSettings = function () {
    if (vm.smsSettings.authId && !vm.smsSettings.smsProviderType) { vm.smsSettings.smsProviderType = 'PLIVO' }
    if (!vm.smsSettings.authId && !vm.smsSettings.fromPhone) { vm.smsSettings.smsProviderType = null }
    return $http.put(vm.smsSettingsUrl, vm.smsSettings)
      .catch(rejection => alertLiteSvc.alertError(rejection?.statusText))
  }

  vm.saveDcbSmsSettings = function () {
    if (vm.dcbSmsSettings.enabled && !vm.dcbSmsSettings.smsProviderType) { vm.dcbSmsSettings.smsProviderType = 'TRUE_SMS_GW' }
    if (!vm.dcbSmsSettings.enabled) vm.dcbSmsSettings.smsProviderType = null
    return $http.put(vm.dcbSmsSettingsUrl, { ...vm.dcbSmsSettings, senderType: vm.dcbSmsSettings.senderType?.value })
      .catch(rejection => alertLiteSvc.alertError(rejection?.statusText))
  }

  vm.saveResetPasswordSettings = function () {
    return $http.post('/nora/api/settings/login-options/reset-password', { resetPasswordEnabled: !!vm.inapp?.resetPasswordSettings?.resetPasswordEnabled })
      .catch(rejection => alertLiteSvc.alertError(rejection?.statusText))
  }

  // load settings
  Setting.loadAll()
    .to(vm.settings = {})
    .then(
      () => {
        // Backend may omit these keys for older networks; initialize safe UI defaults.
        if (!vm.settings.OFFLINE_PLAYBACK_MAX_COUNT) {
          vm.settings.OFFLINE_PLAYBACK_MAX_COUNT = { name: 'OFFLINE_PLAYBACK_MAX_COUNT', value: 20, useSystemDefault: false }
        }
        if (!vm.settings.OFFLINE_PLAYBACK_MAX_QUALITY) {
          vm.settings.OFFLINE_PLAYBACK_MAX_QUALITY = { name: 'OFFLINE_PLAYBACK_MAX_QUALITY', value: 'HD', useSystemDefault: false }
        }
        // save settings in order to compare later what was changed
        vm.originals = angular.copy(vm.settings)
        vm.paymentVoidEnabled = vm.settings.PAYMENT_VOID_HOURS.value !== 0
        $scope.loading = false
      }
    )

  vm.loadSmsSettings = (function () {
    return vm.smsGatewayEnabled && $http.get(vm.smsSettingsUrl)
      .data()
      .then((resp) => resp.payload)
      .to(vm.smsSettings = {})
      .catch(rejection => alertLiteSvc.alertError(rejection?.statusText))
  })()

  vm.loadDcbSmsSettings = (function () {
    return vm.dcbSmsGatewayEnabled && $http.get(vm.dcbSmsSettingsUrl)
      .data()
      .then((resp) => {
        vm.passwordRequired = !resp.payload.authId
        return { ...resp.payload, enabled: !!resp.payload.smsProviderType, senderType: vm.senderTypes.find(type => type.value === resp.payload.senderType) }
      })
      .to(vm.dcbSmsSettings = {})
      .catch(rejection => alertLiteSvc.alertError(rejection?.statusText))
  })()

  // in-app registration extra data
  InappSettings.loadExtra()
    .to(vm.inapp_extra = {
      networks: [],
      timeUnits: [],
      subscriptions: {}
    })

  // load in-app registration settings
  vm.inapp_error = null // default values
  InappSettings.forceLoad()
    .then((inapp) => (vm.inapp = inapp))
    .then(() => vm.inapp.inAppRegistrationSettings.network && vm.loadSubscriptionsFor(vm.inapp.inAppRegistrationSettings, false))
    .then(() => vm.inapp.guestModeSettings.network && vm.loadSubscriptionsFor(vm.inapp.guestModeSettings, false))
    .catch((error) => (vm.inapp_error = error))

  /**
   * Load subscriptions list for network
   *
   * @memberof mwApp/settings.controller:GeneralSettingsCtrl
   */
  vm.loadSubscriptionsFor = (mode, onChange = true) => {
    if (onChange) {
      mode.subscription = null
    }

    if (!has(mode.network.id)(vm.inapp_extra.subscriptions)) {
      InappSettings.loadExtra(`/${mode.network.id}/subscriptions`)
        .then(({ subscriptions }) => {
          vm.inapp_extra.subscriptions[mode.network.id] = subscriptions
        })
    }
  }

  /**
   * Popup for maxActivationLength error
   *
   * @memberof mwApp/settings.controller:GeneralSettingsCtrl
   * @function
   */
  vm.openPopup = function (text, props = {}) {
    $uibModal.open({
      templateUrl: 'components/common/views/popup.html',
      controller: 'PopupCtrl as vm',
      animation: false,
      size: 'lg',
      resolve: {
        text: function () { return text },
        props: function () { return props }
      }
    })
  }

  /**
   * Update settings
   *
   * @memberof mwApp/settings.controller:GeneralSettingsCtrl
   */
  vm.submit = function () {
    vm.setDisable()
    if (!vm.paymentVoidEnabled) {
      vm.settings.PAYMENT_VOID_HOURS.value = 0
    }

    if (vm.PAYMENT_LENGTH_UNLIMITED && vm.settings.UNLIMITED_SUBSCRIPTION_ENABLED.value) {
      vm.settings.ACTIVATION_LENGTH_LIMIT.value = vm.originals.ACTIVATION_LENGTH_LIMIT.value
    }

    const requests = [
      vm.saveEmailSettings(),
      vm.smsServiceSettings.enabled && vm.saveTrialSmsSettings(),
      Setting.saveAll(vm.settings, vm.originals),
      vm.smsGatewayEnabled && !vm.settings.MULTIORG_ENABLED.value ? vm.saveSmsSettings() : $q.when(),
      vm.dcbSmsGatewayEnabled && !vm.settings.MULTIORG_ENABLED.value ? vm.saveDcbSmsSettings() : $q.when(),
      !vm.settings.MULTIORG_ENABLED.value ? vm.saveResetPasswordSettings() : $q.when()
    ]

    // Only add EPG settings request if it's enabled
    if (vm.epgSettingsEnabled) {
      requests.push($http.put('/nora/api/settings/epg', { minProgramDurationInSeconds: vm.minProgramDuration }))
    }

    $q.all(requests)
      .then(() => {
        if (vm.inapp_error == null && !vm.settings.MULTIORG_ENABLED.value) {
          InappSettings.save(vm.inapp).then(() => {
            storageSvc.local.set('timezone', vm.settings.SYSTEM_TIMEZONE.value)
            alertLiteSvc.alert('Settings updated', 'success')
            vm.location.reload()
          })
        } else {
          alertLiteSvc.alert('Settings updated', 'success')
          vm.location.reload()
        }
      })
      .finally(vm.unsetDisable)
  }

  vm.enableVerifyEmail = function (form) {
    return ['emailaddress', 'emailpassword', 'emailhost', 'emailport'].some(field => form[field].$dirty)
  }

  vm.enableVerifyTrialSms = function (form) {
    return ['trialFromPhone', 'trialMessageTemplate'].some(field => form[field].$dirty)
  }

  vm.verifyEmailUrl = '/nora/api/settings/mail/verify'

  vm.verifyEmail = function () {
    vm.verifyEmailSpinner = true

    $http.post(vm.verifyEmailUrl, {})
      .data()
      .then((resp) => resp.payload)
      .then(data => {
        vm.emailServerSettingsChecked = true
        vm.emailServerSettingsValid = data.result === 'OK'
        vm.emailServerSettingsMessage = data.message
      })
      .catch(rejection => alertLiteSvc.alertError(rejection?.statusText))
      .finally(() => {
        vm.verifyEmailSpinner = false
      })
  }
  vm.mailSettings = mailSettings

  // Load EPG settings
  vm.epgSettingsEnabled = true
  $http.get('/nora/api/settings/epg')
    .data()
    .then((resp) => resp.payload)
    .then(data => {
      vm.minProgramDuration = data.minProgramDurationInSeconds || 1
    })
    .catch(() => {
      vm.epgSettingsEnabled = false
    })

  vm.saveEmailSettings = function () {
    if (!vm.mailSettings.password) {
      vm.mailSettings.password = null
    }

    const urlEmailSettings = '/nora/api/settings/mail'
    return $http.put(urlEmailSettings, vm.mailSettings)
      .catch(rejection => alertLiteSvc.alertError(rejection?.statusText))
  }

  vm.verifySmsTrial = function () {
    if (!vm.smsServiceVerify) return

    vm.verifySmsTrialSpinner = true

    const url = '/nora/api/settings/sms/trial/test'

    $http.post(url, { phone: vm.smsServiceVerify })
      .data()
      .then(() => alertLiteSvc.alert('Sms service tested', 'info'))
      .catch(rejection => alertLiteSvc.alertError(rejection?.statusText))
      .finally(() => {
        vm.verifySmsTrialSpinner = false
        vm.smsServiceVerify = ''
      })
  }

  vm.saveTrialSmsSettings = function () {
    const urlEmailSettings = '/nora/api/settings/sms/trial'
    return $http.put(urlEmailSettings, vm.smsServiceSettings)
      .catch(rejection => alertLiteSvc.alertError(rejection?.statusText))
  }

  $scope.$watch('vm.settings.MULTIORG_ENABLED.value', (newValue, oldValue) => {
    if (oldValue !== undefined) {
      if (vm.settings.MULTIORG_ENABLED && vm.settings.MULTIORG_ENABLED.value) {
        $uibModal.open({
          templateUrl: 'components/settings/views/multiTenantPopup.html',
          controller: 'PopupCtrl as vm',
          animation: false,
          size: 'md',
          resolve: {
            text: function () { return '' },
            props: function () { return '' }
          }
        })
      }
    }
  })

  vm.storedPassword = null

  vm.viewPassword = function () {
    vm.passVisible = !vm.passVisible

    if (vm.passVisible) {
      if (vm.storedPassword !== null) {
        vm.mailSettings.password = vm.storedPassword
      } else {
        $http.get('/nora/api/settings/mail/pass')
          .data()
          .then((resp) => resp.payload)
          .then(data => {
            vm.mailSettings.password = vm.storedPassword = data.password
          })
          .catch(rejection => alertLiteSvc.alertError(rejection?.statusText))
      }
    }
  }

  vm.copyString = function (model, text = '') {
    navigator.clipboard.writeText(model)
    alertLiteSvc.alert('Copied ' + text, 'success')
  }

  // Tooltip for strong password policy (HTML list, used in addPackage.html)
  vm.strongPwdTooltip = $sce.trustAsHtml(
    '<div style="padding: 12px">' +
    '<div>Subscriber’s password must comply with the following security requirements:</div>' +
    '<ul style="margin:6px 0 0 18px;padding:0;">' +
    '<li>Min 8 symbols</li>' +
    '<li>At least one uppercase and lowercase Latin letter</li>' +
    '<li>At least one digit (0–9)</li>' +
    '<li>At least one symbol (@#$_&-*!?.)</li>' +
    '</ul>' +
    '</div>'
  )

  // Tooltip for default password policy (two lines)
  vm.defaultPwdTooltip = $sce.trustAsHtml(
    '<div style="padding: 12px">' +
    '<div>Subscriber’s password must comply with the following security requirements:</div>' +
    '<ul style="margin:6px 0 0 18px;padding:0;">' +
    '<li>Min 6 symbols</li>' +
    '<li>Only Latin letters, numbers (0-9) and symbols (@#$_&-*!?.)</li>' +
    '</ul>' +
    '</div>'
  )
}
